import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../components/Layout';
import { toast } from 'react-toastify';
import { get, post } from '../../controllers/apiController';
import axios from 'axios';
import { ContactAvatar } from '../../components/Header';

const API_BASE = 'https://levelupapi.levelupcrm.com.tr';

// ── Şablon yardımcı fonksiyonları ─────────────────────────────────────────────
function uniqueVars(text) {
    return [...new Set([...(text || '').matchAll(/\{\{([^}]+)\}\}/g)].map(m => m[1]))];
}

function extractTplVars(tmpl, lead = {}) {
    if (!tmpl) return {};
    const comps = tmpl.components || [];
    const vars = {};
    const defs = [lead.adi_soyadi || '', lead.firma_adi || '', lead.telefon || ''];
    const header = comps.find(c => c.type === 'HEADER');
    if (header?.format === 'TEXT') uniqueVars(header.text).forEach((v, i) => { vars[`h_${v}`] = defs[i] || ''; });
    const body = comps.find(c => c.type === 'BODY');
    let bodyVars = uniqueVars(body?.text);
    if (bodyVars.length === 0 && body?.example?.body_text?.[0]?.length > 0)
        bodyVars = body.example.body_text[0].map((_, i) => String(i + 1));
    bodyVars.forEach((v, i) => { vars[`b_${v}`] = defs[i] || ''; });
    const buttons = comps.find(c => c.type === 'BUTTONS');
    (buttons?.buttons || []).forEach((btn, idx) => {
        if (btn.type === 'URL') uniqueVars(btn.url).forEach(v => { vars[`btn_${idx}_${v}`] = ''; });
    });
    return vars;
}

function isNamed(v) { return isNaN(parseInt(v, 10)); }
function textParam(v, text) {
    return isNamed(v) ? { type: 'text', parameter_name: v, text } : { type: 'text', text };
}

function buildTplComponents(tmpl, vars, pdfMediaId, pdfFilename) {
    if (!tmpl) return [];
    const comps = tmpl.components || [];
    const result = [];
    const header = comps.find(c => c.type === 'HEADER');
    if (header?.format === 'IMAGE') {
        const imageUrl = header.example?.header_handle?.[0];
        if (imageUrl) result.push({ type: 'header', parameters: [{ type: 'image', image: { link: imageUrl } }] });
    } else if (header?.format === 'DOCUMENT') {
        if (pdfMediaId) result.push({ type: 'header', parameters: [{ type: 'document', document: { id: pdfMediaId, filename: pdfFilename || 'belge.pdf' } }] });
    } else if (header?.format === 'TEXT') {
        const vrs = uniqueVars(header.text);
        if (vrs.length) result.push({ type: 'header', parameters: vrs.map(v => textParam(v, vars[`h_${v}`] || '')) });
    }
    const body = comps.find(c => c.type === 'BODY');
    let bodyVars = uniqueVars(body?.text);
    if (bodyVars.length === 0 && body?.example?.body_text?.[0]?.length > 0)
        bodyVars = body.example.body_text[0].map((_, i) => String(i + 1));
    if (bodyVars.length) result.push({ type: 'body', parameters: bodyVars.map(v => textParam(v, vars[`b_${v}`] || '')) });
    const buttons = comps.find(c => c.type === 'BUTTONS');
    (buttons?.buttons || []).forEach((btn, idx) => {
        if (btn.type === 'FLOW') result.push({ type: 'button', sub_type: 'flow', index: String(idx), parameters: [{ type: 'action', action: {} }] });
        else if (btn.type === 'URL') {
            const vrs = uniqueVars(btn.url);
            if (vrs.length) result.push({ type: 'button', sub_type: 'url', index: String(idx), parameters: vrs.map(v => textParam(v, vars[`btn_${idx}_${v}`] || '')) });
        }
    });
    return result;
}

function previewTpl(tmpl, vars) {
    if (!tmpl) return '';
    const body = (tmpl.components || []).find(c => c.type === 'BODY');
    let text = body?.text || '';
    [...text.matchAll(/\{\{([^}]+)\}\}/g)].map(m => m[1]).forEach(v => {
        text = text.replace(new RegExp(`\\{\\{${v}\\}\\}`, 'g'), vars[`b_${v}`] || `{{${v}}}`);
    });
    return text;
}

function paramLabel(key) {
    if (key.startsWith('h_')) return `Başlık (${key.slice(2)})`;
    if (key.startsWith('b_')) return `Mesaj (${key.slice(2)})`;
    const m = key.match(/^btn_(\d+)_(.+)$/);
    if (m) return `Buton ${parseInt(m[1]) + 1} URL (${m[2]})`;
    return key;
}

// ── Medya Bileşenleri ──────────────────────────────────────────────────────────
const MediaImage = ({ mediaId, caption }) => {
    const src = `${API_BASE}/whatsapp/media/${mediaId}`;
    return (
        <div>
            <img src={src} alt={caption || 'Resim'}
                style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: '10px', display: 'block', cursor: 'pointer' }}
                onClick={() => window.open(src, '_blank')}
                onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
            <div style={{ display: 'none', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#888', padding: '8px', background: '#f5f5f5', borderRadius: '8px' }}>
                <i className="bi bi-image text-muted" style={{ fontSize: '20px' }}></i>
                <span>{caption || 'Resim'}</span>
            </div>
            {caption && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>{caption}</div>}
        </div>
    );
};

const MediaDocument = ({ mediaId, caption, text }) => {
    const src = `${API_BASE}/whatsapp/media/${mediaId}`;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: 'rgba(0,0,0,0.04)', borderRadius: '10px' }}>
            <div style={{ width: '40px', height: '40px', background: '#fee2e2', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-file-earmark-pdf text-danger" style={{ fontSize: '20px' }}></i>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text || caption || 'Doküman'}</div>
                {caption && caption !== text && <div style={{ fontSize: '11px', color: '#888' }}>{caption}</div>}
            </div>
            {mediaId && (
                <a href={src} target="_blank" rel="noreferrer"
                    style={{ width: '32px', height: '32px', background: '#eff6ff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1d4ed8', flexShrink: 0, textDecoration: 'none' }}>
                    <i className="feather-download" style={{ fontSize: '14px' }}></i>
                </a>
            )}
        </div>
    );
};

const MediaAudio = ({ mediaId }) => {
    const src = `${API_BASE}/whatsapp/media/${mediaId}`;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
            <div style={{ width: '32px', height: '32px', background: '#f3f4f6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-mic-fill" style={{ fontSize: '14px', color: '#6b7280' }}></i>
            </div>
            <audio controls src={src} style={{ height: '32px', flex: 1, maxWidth: '220px' }}>Ses mesajı</audio>
        </div>
    );
};

const MediaLocation = ({ mesaj }) => {
    let loc = null;
    try { loc = JSON.parse(mesaj); } catch { return null; }
    if (!loc?.lat || !loc?.lng) return null;
    const mapsUrl = `https://www.google.com/maps?q=${loc.lat},${loc.lng}`;
    const osmUrl = `https://staticmap.openstreetmap.de/staticmap.php?center=${loc.lat},${loc.lng}&zoom=15&size=280x150&markers=${loc.lat},${loc.lng},red-pushpin`;
    return (
        <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none' }}>
            <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', maxWidth: '260px' }}>
                <div style={{ position: 'relative', background: '#e8f5e9', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    <img src={osmUrl} alt="Konum" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: '#ef4444', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}>
                        <i className="bi bi-geo-alt-fill" style={{ color: '#fff', fontSize: '14px' }}></i>
                    </div>
                </div>
                <div style={{ padding: '8px 12px', background: '#fff' }}>
                    {loc.name && <div style={{ fontWeight: 700, fontSize: '12px', color: '#111', marginBottom: '2px' }}>{loc.name}</div>}
                    {loc.address && <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{loc.address}</div>}
                    <div style={{ fontSize: '10px', color: '#10b981', fontWeight: 600 }}>
                        <i className="bi bi-geo-alt me-1"></i>Haritada Gör
                    </div>
                </div>
            </div>
        </a>
    );
};

const MessageMedia = ({ msg }) => {
    const { Medya_ID, Medya_Tur, Mesaj } = msg;
    if (Medya_Tur === 'location') return <MediaLocation mesaj={Mesaj} />;
    if (!Medya_ID) return null;
    if (Medya_Tur === 'image') return <MediaImage mediaId={Medya_ID} caption={Mesaj !== '[Resim]' ? Mesaj : ''} />;
    if (Medya_Tur === 'document') return <MediaDocument mediaId={Medya_ID} text={Mesaj} />;
    if (Medya_Tur === 'audio') return <MediaAudio mediaId={Medya_ID} />;
    if (Medya_Tur === 'video') {
        const src = `${API_BASE}/whatsapp/media/${Medya_ID}`;
        return <video controls style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: '12px' }}><source src={src} /></video>;
    }
    return null;
};

// ── Mesaj Balonu ───────────────────────────────────────────────────────────────
const MessageBubble = ({ msg }) => {
    const isOutgoing = msg.Yon === 'giden';
    const hasMedia = !!msg.Medya_ID || msg.Medya_Tur === 'location';
    const showText = !hasMedia || (
        msg.Mesaj && msg.Mesaj !== '[Resim]' &&
        !msg.Mesaj.startsWith('[Doküman:') &&
        msg.Mesaj !== '[Ses mesajı]' && msg.Mesaj !== '[Video]' &&
        msg.Medya_Tur !== 'location'
    );

    return (
        <div style={{ display: 'flex', justifyContent: isOutgoing ? 'flex-end' : 'flex-start', marginBottom: '2px' }}>
            <div style={{
                maxWidth: '68%',
                background: isOutgoing ? '#dcf8c6' : '#ffffff',
                borderRadius: isOutgoing ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                padding: '8px 12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                fontSize: '13.5px',
                color: '#111',
                position: 'relative'
            }}>
                {msg.Tur === 'sablon' && (
                    <div style={{ fontSize: '10px', color: '#25D366', fontWeight: 700, marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '4px', letterSpacing: '0.3px' }}>
                        <i className="bi bi-layout-text-window"></i> ŞABLON: {msg.Sablon_Adi}
                    </div>
                )}
                {hasMedia && <div style={{ marginBottom: showText ? '8px' : 0 }}><MessageMedia msg={msg} /></div>}
                {showText && <div style={{ lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.Mesaj}</div>}
                <div style={{ fontSize: '10px', color: '#9ca3af', textAlign: 'right', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '3px' }}>
                    {new Date(msg.Tarih).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    {isOutgoing && (() => {
                        const isRead = msg.Durum === 'okundu' || msg.Durum === 'read';
                        const isDelivered = isRead || msg.Durum === 'iletildi' || msg.Durum === 'delivered';
                        const isFailed = msg.Durum === 'basarisiz' || msg.Durum === 'failed';
                        if (isFailed) return <span style={{ color: '#ef4444', fontSize: '12px' }} title="İletilemedi">✗</span>;
                        return (
                            <span style={{ color: isRead ? '#34b7f1' : '#9ca3af', fontSize: '12px', fontWeight: 700 }}>
                                {isDelivered ? '✓✓' : '✓'}
                            </span>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
};

// ── Tarih Ayracı ───────────────────────────────────────────────────────────────
const DateDivider = ({ date }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '12px 0 8px' }}>
        <div style={{ flex: 1, height: '1px', background: 'rgba(0,0,0,0.08)' }} />
        <span style={{
            fontSize: '11px', color: '#6b7280', background: '#e5e7eb',
            padding: '3px 10px', borderRadius: '10px', fontWeight: 600, whiteSpace: 'nowrap'
        }}>{date}</span>
        <div style={{ flex: 1, height: '1px', background: 'rgba(0,0,0,0.08)' }} />
    </div>
);

function formatDateLabel(dateStr) {
    const d = new Date(dateStr);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === now.toDateString()) return 'Bugün';
    if (d.toDateString() === yesterday.toDateString()) return 'Dün';
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function groupMessagesByDate(messages) {
    const groups = [];
    let lastDate = null;
    messages.forEach(msg => {
        const msgDate = new Date(msg.Tarih).toDateString();
        if (msgDate !== lastDate) {
            groups.push({ type: 'date', date: formatDateLabel(msg.Tarih) });
            lastDate = msgDate;
        }
        groups.push({ type: 'msg', msg });
    });
    return groups;
}

// ── Ana Bileşen ────────────────────────────────────────────────────────────────
const WhatsappInbox = () => {
    const [conversations, setConversations] = useState([]);
    const [selectedPhone, setSelectedPhone] = useState(null);
    const [selectedConv, setSelectedConv] = useState(null);
    const [messages, setMessages] = useState([]);
    const [waTemplates, setWaTemplates] = useState([]);
    const [waTab, setWaTab] = useState('text');
    const [is24hOpen, setIs24hOpen] = useState(false);
    const [messageText, setMessageText] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isLoadingConvs, setIsLoadingConvs] = useState(true);
    const [isLoadingMsgs, setIsLoadingMsgs] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateVars, setTemplateVars] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [totalUnread, setTotalUnread] = useState(0);
    const [pdfFile, setPdfFile] = useState(null);
    const [pdfCaption, setPdfCaption] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    const pdfInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        loadConversations();
        get('whatsapp/templates').then(r => setWaTemplates(r?.templates || [])).catch(() => {});
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Aktif konuşmada 10 saniyede bir yeni mesaj / durum güncellemesi çek
    useEffect(() => {
        if (!selectedPhone) return;
        const interval = setInterval(async () => {
            try {
                const r = await get(`whatsapp/messages/${encodeURIComponent(selectedPhone)}`);
                const fresh = r?.messages || [];
                setMessages(prev => {
                    const prevIds = prev.map(m => m.Mesaj_ID).join(',');
                    const freshIds = fresh.map(m => m.Mesaj_ID).join(',');
                    if (prevIds !== freshIds) return fresh;
                    // Sadece Durum alanlarını güncelle
                    return prev.map(m => {
                        const upd = fresh.find(f => f.Mesaj_ID === m.Mesaj_ID);
                        return upd && upd.Durum !== m.Durum ? { ...m, Durum: upd.Durum } : m;
                    });
                });
            } catch {}
        }, 10000);
        return () => clearInterval(interval);
    }, [selectedPhone]);

    const loadConversations = async () => {
        setIsLoadingConvs(true);
        try {
            const r = await get('whatsapp/conversations');
            const convs = r?.conversations || [];
            setConversations(convs);
            setTotalUnread(convs.reduce((sum, c) => sum + (parseInt(c.OkunmamisSayisi) || 0), 0));
        } catch (err) {
            const detail = err?.response?.data?.error || err?.response?.data?.message || err.message || '';
            toast.error(`Konuşmalar yüklenemedi${detail ? ': ' + detail : ''}`);
        }
        setIsLoadingConvs(false);
    };

    const selectConversation = async (conv) => {
        setSelectedPhone(conv.Telefon);
        setSelectedConv(conv);
        setIsLoadingMsgs(true);
        setMessages([]);
        if (parseInt(conv.OkunmamisSayisi) > 0) {
            post(`whatsapp/mark-read/${encodeURIComponent(conv.Telefon)}`).catch(() => {});
            setConversations(prev => prev.map(c => c.Telefon === conv.Telefon ? { ...c, OkunmamisSayisi: 0 } : c));
            setTotalUnread(prev => Math.max(0, prev - (parseInt(conv.OkunmamisSayisi) || 0)));
        }
        try {
            const r = await get(`whatsapp/messages/${encodeURIComponent(conv.Telefon)}`);
            const msgs = r?.messages || [];
            setMessages(msgs);
            // 24 saatlik pencere: son gelen mesaj 24 saatten yeni mi?
            const lastIncoming = msgs.filter(m => m.Yon === 'gelen').pop();
            const open = lastIncoming && (Date.now() - new Date(lastIncoming.Tarih).getTime()) < 24 * 60 * 60 * 1000;
            setIs24hOpen(!!open);
            setWaTab(open ? 'text' : 'template');
        } catch { toast.error('Mesajlar yüklenemedi'); }
        setIsLoadingMsgs(false);
    };

    const handleSendText = async () => {
        if (!messageText.trim() || !selectedPhone) return;
        setIsSending(true);
        try {
            await post('whatsapp/send-message', { phone: selectedPhone, message: messageText.trim(), formId: selectedConv?.Form_ID || null });
            toast.success('Mesaj gönderildi!');
            setMessageText('');
            const r = await get(`whatsapp/messages/${encodeURIComponent(selectedPhone)}`);
            setMessages(r?.messages || []);
            loadConversations();
        } catch (err) { toast.error(err?.response?.data?.message || 'Mesaj gönderilemedi!'); }
        setIsSending(false);
    };

    const handleSelectTemplate = (tmpl) => {
        setSelectedTemplate(tmpl);
        const lead = { adi_soyadi: selectedConv?.Adi_Soyadi || '', telefon: selectedConv?.Telefon || '' };
        setTemplateVars(extractTplVars(tmpl, lead));
        setPdfFile(null);
    };

    const templateHasDocHeader = () => {
        const h = (selectedTemplate?.components || []).find(c => c.type === 'HEADER');
        return h?.format === 'DOCUMENT';
    };

    const handleSendTemplate = async () => {
        if (!selectedTemplate || !selectedPhone) return;
        setIsSending(true);
        try {
            let pdfMediaId = null, pdfFilename = null;
            if (templateHasDocHeader() && pdfFile) {
                setIsUploading(true);
                const fd = new FormData();
                fd.append('file', pdfFile);
                const token = localStorage.getItem('token');
                const uploadRes = await axios.post(`${API_BASE}/whatsapp/upload-media`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
                pdfMediaId = uploadRes.data.mediaId;
                pdfFilename = pdfFile.name;
                setIsUploading(false);
            }
            const components = buildTplComponents(selectedTemplate, templateVars, pdfMediaId, pdfFilename);
            await post('whatsapp/send-template', { phone: selectedPhone, templateName: selectedTemplate.name, langCode: selectedTemplate.language || 'tr', components, formId: selectedConv?.Form_ID || null });
            toast.success('Şablon gönderildi!');
            setSelectedTemplate(null);
            setTemplateVars({});
            setPdfFile(null);
            setWaTab('text');
            const r = await get(`whatsapp/messages/${encodeURIComponent(selectedPhone)}`);
            setMessages(r?.messages || []);
            loadConversations();
        } catch (err) {
            setIsUploading(false);
            toast.error(`Şablon gönderilemedi: ${err?.response?.data?.error || err?.response?.data?.message || err.message || 'Bilinmeyen hata'}`);
        }
        setIsSending(false);
    };

    const handleSendFile = async () => {
        if (!pdfFile || !selectedPhone) return;
        setIsSending(true);
        setIsUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', pdfFile);
            const token = localStorage.getItem('token');
            const uploadRes = await axios.post(`${API_BASE}/whatsapp/upload-media`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
            setIsUploading(false);
            const isImg = pdfFile.type.startsWith('image/');
            if (isImg) {
                await post('whatsapp/send-image', { phone: selectedPhone, mediaId: uploadRes.data.mediaId, caption: pdfCaption.trim() || undefined, formId: selectedConv?.Form_ID || null });
            } else {
                await post('whatsapp/send-document', { phone: selectedPhone, mediaId: uploadRes.data.mediaId, filename: pdfFile.name, caption: pdfCaption.trim() || undefined, formId: selectedConv?.Form_ID || null });
            }
            toast.success(isImg ? 'Resim gönderildi!' : 'Dosya gönderildi!');
            setPdfFile(null);
            setPdfCaption('');
            const r = await get(`whatsapp/messages/${encodeURIComponent(selectedPhone)}`);
            setMessages(r?.messages || []);
            loadConversations();
        } catch (err) {
            setIsUploading(false);
            toast.error(err?.response?.data?.message || 'Dosya gönderilemedi!');
        }
        setIsSending(false);
    };

    const filteredConversations = conversations.filter(c => {
        const q = searchQuery.toLowerCase();
        return !q || (c.Adi_Soyadi || '').toLowerCase().includes(q) || (c.Telefon || '').includes(q);
    });

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const now = new Date();
        if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
        if (d.toDateString() === yesterday.toDateString()) return 'Dün';
        return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
    };

    const getLastMsgPreview = (conv) => {
        if (conv.SonMedyaTur === 'image') return '📷 Fotoğraf';
        if (conv.SonMedyaTur === 'document') return '📄 Doküman';
        if (conv.SonMedyaTur === 'audio') return '🎤 Ses mesajı';
        if (conv.SonMedyaTur === 'video') return '🎬 Video';
        if (conv.SonMedyaTur === 'location') return '📍 Konum';
        return conv.SonMesaj || '—';
    };

    const groupedMessages = groupMessagesByDate(messages);

    return (
        <Layout>
            <main className="nxl-container">
                <div className="nxl-content p-0" style={{ height: '100%' }}>
                    <div style={{
                        display: 'flex', height: 'calc(100vh - 80px)',
                        background: '#f0f2f5', borderRadius: '0',
                        overflow: 'hidden'
                    }}>

                        {/* ─── Sol Panel: Konuşma Listesi ─────────────────── */}
                        <div style={{
                            width: '340px', flexShrink: 0, display: 'flex', flexDirection: 'column',
                            background: '#fff', borderRight: '1px solid #e5e7eb'
                        }}>
                            {/* Header */}
                            <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #f0f0f0' }}>
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #25D366, #128C7E)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <i className="bi bi-whatsapp text-white" style={{ fontSize: '18px' }}></i>
                                        </div>
                                        <div>
                                            <div className="fw-bold" style={{ fontSize: '15px', color: '#111' }}>WhatsApp</div>
                                            <div style={{ fontSize: '11px', color: '#888' }}>
                                                {conversations.length} konuşma
                                                {totalUnread > 0 && <span style={{ marginLeft: '6px', background: '#25D366', color: '#fff', borderRadius: '8px', padding: '1px 7px', fontSize: '10px', fontWeight: 700 }}>{totalUnread}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-sm"
                                        style={{ background: '#f3f4f6', border: 'none', borderRadius: '8px', padding: '6px 10px', color: '#374151' }}
                                        onClick={loadConversations}
                                        title="Yenile">
                                        <i className="feather-refresh-cw" style={{ fontSize: '14px' }}></i>
                                    </button>
                                </div>
                                {/* Arama */}
                                <div style={{ position: 'relative' }}>
                                    <i className="feather-search" style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '14px' }}></i>
                                    <input
                                        type="text"
                                        style={{
                                            width: '100%', padding: '8px 12px 8px 34px',
                                            borderRadius: '20px', border: '1px solid #e5e7eb',
                                            fontSize: '13px', background: '#f9fafb', outline: 'none',
                                            transition: 'border 0.15s'
                                        }}
                                        placeholder="Kişi veya numara ara..."
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        onFocus={e => e.target.style.borderColor = '#25D366'}
                                        onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                                    />
                                </div>
                            </div>

                            {/* Liste */}
                            <div style={{ flex: 1, overflowY: 'auto' }}>
                                {isLoadingConvs ? (
                                    Array.from({ length: 6 }).map((_, i) => (
                                        <div key={i} style={{ padding: '12px 16px', borderBottom: '1px solid #f9f9f9', display: 'flex', gap: '12px', alignItems: 'center' }}>
                                            <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: '#f3f4f6', flexShrink: 0 }}></div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ height: '12px', background: '#f3f4f6', borderRadius: '6px', width: '60%', marginBottom: '8px' }}></div>
                                                <div style={{ height: '10px', background: '#f9fafb', borderRadius: '6px', width: '80%' }}></div>
                                            </div>
                                        </div>
                                    ))
                                ) : filteredConversations.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#9ca3af' }}>
                                        <i className="bi bi-chat-square" style={{ fontSize: '36px', marginBottom: '10px', opacity: 0.5 }}></i>
                                        <div style={{ fontSize: '13px' }}>Konuşma bulunamadı</div>
                                    </div>
                                ) : filteredConversations.map(conv => {
                                    const unread = parseInt(conv.OkunmamisSayisi) || 0;
                                    const isSelected = selectedPhone === conv.Telefon;
                                    const convOpen = conv.SonYon === 'gelen' && (Date.now() - new Date(conv.SonTarih).getTime()) < 24 * 60 * 60 * 1000;
                                    return (
                                        <div
                                            key={conv.Telefon}
                                            onClick={() => selectConversation(conv)}
                                            style={{
                                                padding: '12px 16px',
                                                borderBottom: '1px solid #f5f5f5',
                                                cursor: 'pointer',
                                                background: isSelected ? '#f0fdf4' : 'transparent',
                                                borderLeft: `3px solid ${isSelected ? '#25D366' : unread > 0 ? '#f59e0b' : 'transparent'}`,
                                                transition: 'all 0.12s',
                                            }}
                                            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                                            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                                        >
                                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                                    <ContactAvatar
                                                        name={conv.Adi_Soyadi} phone={conv.Telefon}
                                                        photoUrl={conv.Profil_Foto_URL} size={46} fontSize={17} />
                                                    {unread > 0 ? (
                                                        <div style={{ position: 'absolute', top: '-1px', right: '-1px', width: '14px', height: '14px', background: '#25D366', borderRadius: '50%', border: '2px solid #fff' }} />
                                                    ) : (
                                                        <div title={convOpen ? 'Pencere açık' : 'Pencere kapalı — şablon gerekli'} style={{ position: 'absolute', bottom: '-1px', right: '-1px', width: '12px', height: '12px', background: convOpen ? '#10b981' : '#f59e0b', borderRadius: '50%', border: '2px solid #fff' }} />
                                                    )}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
                                                        <div style={{ fontWeight: unread > 0 ? 700 : 600, fontSize: '13.5px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>
                                                            {conv.Adi_Soyadi || conv.Telefon}
                                                        </div>
                                                        <div style={{ fontSize: '10.5px', color: unread > 0 ? '#f59e0b' : '#9ca3af', flexShrink: 0, fontWeight: unread > 0 ? 700 : 400 }}>
                                                            {formatTime(conv.SonTarih)}
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div style={{ fontSize: '12px', color: unread > 0 ? '#374151' : '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '190px', fontWeight: unread > 0 ? 600 : 400 }}>
                                                            {conv.SonYon === 'giden' && <span style={{ color: '#25D366', marginRight: '3px', fontSize: '11px' }}>✓✓</span>}
                                                            {getLastMsgPreview(conv)}
                                                        </div>
                                                        {unread > 0 ? (
                                                            <span style={{ fontSize: '11px', background: '#25D366', color: '#fff', borderRadius: '10px', padding: '1px 7px', flexShrink: 0, fontWeight: 700, minWidth: '20px', textAlign: 'center' }}>
                                                                {unread}
                                                            </span>
                                                        ) : conv.MesajSayisi ? (
                                                            <span style={{ fontSize: '10px', color: '#9ca3af', flexShrink: 0 }}>
                                                                {conv.MesajSayisi}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    {conv.Form_ID && (
                                                        <a href={`./lead-detail?id=${conv.Form_ID}`} onClick={e => e.stopPropagation()}
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', marginTop: '3px', fontSize: '10px', color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>
                                                            <i className="feather-external-link" style={{ fontSize: '9px' }}></i>Lead Detay
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ─── Sağ Panel: Sohbet ──────────────────────────── */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            {selectedPhone ? (
                                <>
                                    {/* Sohbet Başlığı */}
                                    <div style={{
                                        padding: '12px 20px', background: '#075E54',
                                        display: 'flex', alignItems: 'center', gap: '14px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 1, flexShrink: 0
                                    }}>
                                        <ContactAvatar name={selectedConv?.Adi_Soyadi} phone={selectedPhone} photoUrl={selectedConv?.Profil_Foto_URL} size={44} fontSize={17} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 700, fontSize: '15px', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {selectedConv?.Adi_Soyadi || selectedPhone}
                                            </div>
                                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '1px' }}>{selectedPhone}</div>
                                        </div>
                                        {selectedConv?.Form_ID && (
                                            <a href={`./lead-detail?id=${selectedConv.Form_ID}`}
                                                style={{
                                                    color: '#fff', fontSize: '12px', textDecoration: 'none',
                                                    display: 'flex', alignItems: 'center', gap: '6px',
                                                    background: 'rgba(255,255,255,0.15)', padding: '7px 14px',
                                                    borderRadius: '20px', fontWeight: 600, flexShrink: 0,
                                                    backdropFilter: 'blur(4px)'
                                                }}>
                                                <i className="feather-user" style={{ fontSize: '13px' }}></i>Lead Detay
                                            </a>
                                        )}
                                    </div>

                                    {/* WhatsApp arka plan deseni */}
                                    <div style={{
                                        flex: 1, overflowY: 'auto', padding: '16px 20px',
                                        backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23000000\' fill-opacity=\'0.02\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                                    }}>
                                        {isLoadingMsgs ? (
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div className="spinner-border text-success" style={{ width: '32px', height: '32px' }}></div>
                                                    <div style={{ marginTop: '10px', fontSize: '13px', color: '#6b7280' }}>Mesajlar yükleniyor...</div>
                                                </div>
                                            </div>
                                        ) : messages.length === 0 ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
                                                <div style={{ width: '64px', height: '64px', background: 'rgba(255,255,255,0.8)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                                                    <i className="bi bi-chat-square-dots" style={{ fontSize: '28px', color: '#25D366' }}></i>
                                                </div>
                                                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Henüz mesaj yok</div>
                                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Bu konuşmada hiç mesaj bulunmuyor</div>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                {groupedMessages.map((item, i) =>
                                                    item.type === 'date'
                                                        ? <DateDivider key={`d-${i}`} date={item.date} />
                                                        : <MessageBubble key={i} msg={item.msg} />
                                                )}
                                                <div ref={messagesEndRef} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Mesaj Gönderme Alanı */}
                                    <div style={{ background: '#f0f2f5', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
                                        {/* Tab Bar */}
                                        <div style={{ display: 'flex', background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
                                            {[
                                                { id: 'text', icon: 'feather-edit-3', label: 'Mesaj' },
                                                { id: 'template', icon: 'bi bi-layout-text-window', label: 'Şablon' },
                                                { id: 'pdf', icon: 'feather-paperclip', label: 'Dosya' },
                                            ].map(tab => (
                                                <button key={tab.id} onClick={() => setWaTab(tab.id)}
                                                    style={{
                                                        flex: 1, padding: '10px 8px', border: 'none', cursor: 'pointer',
                                                        fontSize: '12px', fontWeight: 600,
                                                        background: 'transparent',
                                                        color: waTab === tab.id ? '#25D366' : '#9ca3af',
                                                        borderBottom: `2px solid ${waTab === tab.id ? '#25D366' : 'transparent'}`,
                                                        transition: 'all 0.15s',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'
                                                    }}>
                                                    <i className={tab.icon} style={{ fontSize: '13px' }}></i>
                                                    {tab.label}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Serbest Metin */}
                                        {waTab === 'text' && (
                                            <div style={{ padding: '12px 16px' }}>
                                                {!is24hOpen && (
                                                    <div style={{
                                                        marginBottom: '10px', padding: '10px 14px',
                                                        background: '#fef3c7', border: '1px solid #fde68a',
                                                        borderRadius: '10px', display: 'flex', alignItems: 'flex-start', gap: '10px'
                                                    }}>
                                                        <i className="feather-lock" style={{ color: '#d97706', fontSize: '16px', marginTop: '1px', flexShrink: 0 }}></i>
                                                        <div>
                                                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#92400e', marginBottom: '3px' }}>
                                                                24 saatlik pencere kapalı
                                                            </div>
                                                            <div style={{ fontSize: '11px', color: '#78350f', lineHeight: 1.5 }}>
                                                                Bu kişi son 24 saat içinde mesaj göndermedi. Serbest metin mesajlar iletilmez.
                                                                <button onClick={() => setWaTab('template')}
                                                                    style={{ background: 'none', border: 'none', padding: '0 0 0 4px', color: '#d97706', fontWeight: 700, fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>
                                                                    Şablon kullanın →
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#fff', borderRadius: '12px', padding: '8px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', opacity: is24hOpen ? 1 : 0.5 }}>
                                                    <textarea
                                                        ref={textareaRef}
                                                        rows={2}
                                                        maxLength={1024}
                                                        placeholder={is24hOpen ? 'Mesaj yazın... (Shift+Enter yeni satır)' : '24 saat penceresi kapalı — şablon kullanın'}
                                                        value={messageText}
                                                        onChange={e => setMessageText(e.target.value)}
                                                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendText(); } }}
                                                        style={{
                                                            flex: 1, border: 'none', outline: 'none', resize: 'none',
                                                            fontSize: '13.5px', lineHeight: 1.5, padding: 0,
                                                            background: 'transparent', fontFamily: 'inherit'
                                                        }}
                                                    />
                                                    <button
                                                        onClick={handleSendText}
                                                        disabled={isSending || !messageText.trim() || !is24hOpen}
                                                        style={{
                                                            width: '38px', height: '38px', borderRadius: '50%', border: 'none',
                                                            background: (messageText.trim() && is24hOpen) ? '#25D366' : '#e5e7eb',
                                                            color: (messageText.trim() && is24hOpen) ? '#fff' : '#9ca3af',
                                                            cursor: (messageText.trim() && is24hOpen) ? 'pointer' : 'default',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            flexShrink: 0, transition: 'all 0.15s'
                                                        }}>
                                                        {isSending
                                                            ? <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></span>
                                                            : <i className="feather-send" style={{ fontSize: '15px' }}></i>}
                                                    </button>
                                                </div>
                                                {is24hOpen && (
                                                    <div style={{ marginTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div style={{ fontSize: '10px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <i className="feather-check-circle" style={{ fontSize: '10px' }}></i>
                                                            Pencere açık — serbest mesaj gönderilebilir
                                                        </div>
                                                        <div style={{ fontSize: '10px', color: '#9ca3af' }}>{messageText.length}/1024</div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Şablon */}
                                        {waTab === 'template' && (
                                            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                <select
                                                    style={{ padding: '8px 12px', borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '13px', background: '#fff', outline: 'none' }}
                                                    value={selectedTemplate?.name || ''}
                                                    onChange={e => { const t = waTemplates.find(x => x.name === e.target.value); handleSelectTemplate(t || null); }}>
                                                    <option value="">— Şablon seçin —</option>
                                                    {waTemplates.map(t => {
                                                        const h = (t.components || []).find(c => c.type === 'HEADER');
                                                        const badge = h?.format === 'DOCUMENT' ? ' 📄' : h?.format === 'IMAGE' ? ' 🖼️' : '';
                                                        return <option key={t.name} value={t.name}>{t.name}{badge}</option>;
                                                    })}
                                                </select>

                                                {selectedTemplate && (() => {
                                                    const bodyComp = (selectedTemplate.components || []).find(c => c.type === 'BODY');
                                                    const bodyText = bodyComp?.text || '';
                                                    const hasVars = Object.keys(templateVars).length > 0;
                                                    return (
                                                        <>
                                                            {bodyText && (
                                                                <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '10px 12px', fontSize: '12px', color: '#444', border: '1px solid #e9ecef', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                                                                    <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '5px', fontWeight: 700, letterSpacing: '0.5px' }}>ŞABLON METNİ</div>
                                                                    {bodyText}
                                                                </div>
                                                            )}
                                                            {hasVars && (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                    <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 700 }}>DEĞİŞKENLER</div>
                                                                    {Object.keys(templateVars).sort().map(k => (
                                                                        <div key={k} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                                            <span style={{ fontSize: '10px', background: '#dcfce7', color: '#166534', borderRadius: '6px', padding: '3px 8px', fontWeight: 700, flexShrink: 0 }}>
                                                                                {paramLabel(k)}
                                                                            </span>
                                                                            <input
                                                                                type="text"
                                                                                style={{ flex: 1, padding: '5px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px', outline: 'none' }}
                                                                                value={templateVars[k] || ''}
                                                                                onChange={e => setTemplateVars(prev => ({ ...prev, [k]: e.target.value }))}
                                                                                placeholder="Değer girin"
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {templateHasDocHeader() && (
                                                                <div style={{ padding: '10px 12px', borderRadius: '10px', background: '#fff7ed', border: '1px solid #fed7aa' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#c2410c', marginBottom: '6px' }}>
                                                                        <i className="bi bi-file-earmark-pdf me-1"></i>Bu şablon PDF eki gerektiriyor
                                                                    </div>
                                                                    {pdfFile ? (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                            <i className="bi bi-file-earmark-check text-success"></i>
                                                                            <span style={{ fontSize: '12px', flex: 1 }}>{pdfFile.name}</span>
                                                                            <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px', padding: 0 }} onClick={() => setPdfFile(null)}>×</button>
                                                                        </div>
                                                                    ) : (
                                                                        <button style={{ background: '#fff', border: '1px solid #fed7aa', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', color: '#c2410c' }} onClick={() => pdfInputRef.current?.click()}>
                                                                            <i className="bi bi-upload me-1"></i>PDF Seç
                                                                        </button>
                                                                    )}
                                                                    <input ref={pdfInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => setPdfFile(e.target.files[0] || null)} />
                                                                </div>
                                                            )}
                                                            <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
                                                                <div style={{ flex: 1, background: '#dcf8c6', borderRadius: '12px', padding: '10px 14px', fontSize: '12.5px', color: '#111', maxHeight: '80px', overflowY: 'auto', lineHeight: 1.5, whiteSpace: 'pre-wrap', fontStyle: previewTpl(selectedTemplate, templateVars) ? 'normal' : 'italic' }}>
                                                                    {previewTpl(selectedTemplate, templateVars) || <span style={{ color: '#9ca3af' }}>Önizleme burada görünür</span>}
                                                                </div>
                                                                <button
                                                                    onClick={handleSendTemplate}
                                                                    disabled={isSending || isUploading || (templateHasDocHeader() && !pdfFile)}
                                                                    style={{
                                                                        padding: '0 20px', background: '#25D366', border: 'none',
                                                                        borderRadius: '12px', color: '#fff', fontWeight: 700,
                                                                        fontSize: '13px', cursor: 'pointer', display: 'flex',
                                                                        alignItems: 'center', gap: '6px', flexShrink: 0
                                                                    }}>
                                                                    {isUploading ? <><span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></span>Yükleniyor</>
                                                                        : isSending ? <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></span>
                                                                            : <><i className="feather-send" style={{ fontSize: '14px' }}></i>Gönder</>}
                                                                </button>
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        )}

                                        {/* Dosya / Resim Gönder */}
                                        {waTab === 'pdf' && (
                                            <div style={{ padding: '12px 16px' }}>
                                                <div style={{ marginBottom: '10px', fontSize: '11px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <i className="feather-alert-triangle"></i>
                                                    Son 24 saat içinde mesaj almış müşterilere gönderilebilir
                                                </div>
                                                {pdfFile ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', marginBottom: '10px' }}>
                                                        <div style={{ width: '36px', height: '36px', background: pdfFile.type.startsWith('image/') ? '#eff6ff' : '#fee2e2', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                            {pdfFile.type.startsWith('image/')
                                                                ? <i className="bi bi-image text-primary" style={{ fontSize: '18px' }}></i>
                                                                : <i className="bi bi-file-earmark-pdf text-danger" style={{ fontSize: '18px' }}></i>
                                                            }
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pdfFile.name}</div>
                                                            <div style={{ fontSize: '11px', color: '#6b7280' }}>{(pdfFile.size / 1024 / 1024).toFixed(2)} MB</div>
                                                        </div>
                                                        <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', padding: 0 }} onClick={() => setPdfFile(null)}>×</button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => pdfInputRef.current?.click()}
                                                        style={{
                                                            width: '100%', padding: '16px', marginBottom: '10px',
                                                            borderRadius: '10px', border: '2px dashed #e5e7eb',
                                                            background: '#fafafa', cursor: 'pointer', color: '#6b7280',
                                                            fontSize: '13px', display: 'flex', alignItems: 'center',
                                                            justifyContent: 'center', gap: '8px', transition: 'all 0.15s'
                                                        }}
                                                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#25D366'; e.currentTarget.style.color = '#25D366'; }}
                                                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
                                                    >
                                                        <i className="feather-paperclip" style={{ fontSize: '18px' }}></i>
                                                        PDF veya resim seç (max 16 MB)
                                                    </button>
                                                )}
                                                <input ref={pdfInputRef} type="file" accept="application/pdf,image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }} onChange={e => setPdfFile(e.target.files[0] || null)} />
                                                <input
                                                    type="text"
                                                    style={{ width: '100%', padding: '8px 12px', borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '13px', marginBottom: '10px', outline: 'none' }}
                                                    placeholder="Açıklama ekle (isteğe bağlı)"
                                                    value={pdfCaption}
                                                    onChange={e => setPdfCaption(e.target.value)}
                                                />
                                                <button
                                                    onClick={handleSendFile}
                                                    disabled={!pdfFile || isSending || isUploading}
                                                    style={{
                                                        width: '100%', padding: '11px', borderRadius: '10px', border: 'none',
                                                        background: pdfFile ? '#25D366' : '#e5e7eb',
                                                        color: pdfFile ? '#fff' : '#9ca3af',
                                                        fontSize: '13px', fontWeight: 700, cursor: pdfFile ? 'pointer' : 'default',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                                    }}>
                                                    {isUploading ? <><span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></span>Yükleniyor...</>
                                                        : isSending ? <><span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></span>Gönderiliyor...</>
                                                            : <><i className="feather-send" style={{ fontSize: '14px' }}></i>
                                                                {pdfFile?.type.startsWith('image/') ? 'Resim Gönder' : 'Dosya Gönder'}</>}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                /* Boş durum */
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f0f2f5' }}>
                                    <div style={{ textAlign: 'center', maxWidth: '320px' }}>
                                        <div style={{ width: '96px', height: '96px', background: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                                            <i className="bi bi-whatsapp" style={{ fontSize: '48px', color: '#25D366' }}></i>
                                        </div>
                                        <h5 style={{ fontWeight: 700, color: '#111', marginBottom: '8px' }}>WhatsApp Gelen Kutusu</h5>
                                        <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.6, marginBottom: '20px' }}>
                                            Soldan bir konuşmayı seçerek mesajlaşmaya başlayın
                                        </p>
                                        {totalUnread > 0 && (
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '10px', padding: '10px 18px', fontSize: '13px', color: '#92400e', fontWeight: 600 }}>
                                                <div style={{ width: '8px', height: '8px', background: '#f59e0b', borderRadius: '50%' }}></div>
                                                {totalUnread} okunmamış mesaj
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </Layout>
    );
};

export default WhatsappInbox;
